import type { PortalCheckoutAttempt } from "~portal/helpers/portalCheckoutAttempt";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import tenantChanged from "~/store/tenantChanged";
import { clampPortalCartQuantity, portalMinOrderableEaches } from "~portal/helpers/portalEntryUnit";

// region Interfaces

export interface CartItem {
	availableQuantity: number;
	basePrice: number;
	containerSize: string;
	entryEachesPerUnit?: null | number;
	imageUrl: string;
	itemId: string;
	itemName: string;
	minOrderQty: number;
	quantity: number;
	shippingUomLabel?: null | string;
	sku: string;
	unitPrice: number;
}

export interface PortalCheckoutDraft {
	customerId: string;
	contactEmail: string;
	contactPhone: string;
	deliveryDate: string | null;
	note: string;
	poNumber: string;
}

export interface PortalState {
	cart: CartItem[];
	cartOpen: boolean;
	checkoutAttempt?: PortalCheckoutAttempt | null;
	checkoutDetails?: PortalCheckoutDraft | null;
	favorites: string[];
	recentlyViewed: string[];
	selectedLocationId: string | null;
}

// endregion

// region Initial State

const initialState: PortalState = {
	cart: [],
	cartOpen: false,
	checkoutAttempt: null,
	checkoutDetails: null,
	favorites: [],
	recentlyViewed: [],
	selectedLocationId: null
};

// endregion

// region Slice

export const portalSlice = createSlice({
	extraReducers: (builder) => {
		builder.addCase(tenantChanged, (state) => {
			state.selectedLocationId = null;
		});
	},
	initialState,
	name: "portal",
	reducers: {
		abandonCheckoutAttempt: (state) => {
			if (!state.checkoutAttempt?.isRefusedBelowMinimum) state.checkoutAttempt = null;
		},
		addRecentlyViewed: (state, action: PayloadAction<string>) => {
			const itemId = action.payload;
			state.recentlyViewed = [itemId, ...state.recentlyViewed.filter((id) => id !== itemId)].slice(0, 12);
		},
		addToCart: (state, action: PayloadAction<CartItem>) => {
			const existing = state.cart.find((item) => item.itemId === action.payload.itemId);
			if (existing) {
				existing.entryEachesPerUnit = action.payload.entryEachesPerUnit;
				existing.shippingUomLabel = action.payload.shippingUomLabel;
				existing.quantity = clampPortalCartQuantity(
					existing.quantity + action.payload.quantity,
					existing.availableQuantity,
					existing
				);
			} else {
				const capped = { ...action.payload };
				capped.quantity = clampPortalCartQuantity(capped.quantity, capped.availableQuantity, capped);
				state.cart.push(capped);
			}
		},
		clearCart: (state) => {
			state.cart = [];
		},
		removeFromCart: (state, action: PayloadAction<string>) => {
			state.cart = state.cart.filter((item) => item.itemId !== action.payload);
		},
		setCartOpen: (state, action: PayloadAction<boolean>) => {
			state.cartOpen = action.payload;
		},
		setCheckoutAttempt: (state, action: PayloadAction<PortalCheckoutAttempt | null>) => {
			state.checkoutAttempt = action.payload;
		},
		setCheckoutDetails: (state, action: PayloadAction<PortalCheckoutDraft | null>) => {
			state.checkoutDetails = action.payload;
		},
		setCartQuantity: (state, action: PayloadAction<CartItem>) => {
			state.cart = state.cart.filter((item) => item.sku !== action.payload.sku);
			const quantity = clampPortalCartQuantity(
				action.payload.quantity,
				action.payload.availableQuantity,
				action.payload
			);
			if (quantity <= 0) return;
			state.cart.push({ ...action.payload, quantity });
		},
		setPortalTenant: (_state, _action: PayloadAction<string>) => {
			// Prevents cart/favorites bleed across tenants.
			return { ...initialState };
		},
		setSelectedLocation: (state, action: PayloadAction<string | null>) => {
			state.selectedLocationId = action.payload;
		},
		toggleCartOpen: (state) => {
			state.cartOpen = !state.cartOpen;
		},
		toggleFavorite: (state, action: PayloadAction<string>) => {
			const itemId = action.payload;
			const index = state.favorites.indexOf(itemId);
			if (index === -1) {
				state.favorites.push(itemId);
			} else {
				state.favorites.splice(index, 1);
			}
		},
		updateCartQuantity: (state, action: PayloadAction<{ itemId: string; quantity: number; unitPrice?: number }>) => {
			const { itemId, quantity, unitPrice } = action.payload;
			if (quantity <= 0) {
				state.cart = state.cart.filter((item) => item.itemId !== itemId);
			} else {
				const existing = state.cart.find((item) => item.itemId === itemId);
				if (existing) {
					existing.quantity = clampPortalCartQuantity(
						Math.max(quantity, portalMinOrderableEaches(existing.minOrderQty, existing)),
						existing.availableQuantity,
						existing
					);
					if (unitPrice != null) {
						existing.unitPrice = unitPrice;
					}
				}
			}
		}
	}
});

// endregion

// region Actions

export const {
	abandonCheckoutAttempt,
	addRecentlyViewed,
	addToCart,
	clearCart,
	removeFromCart,
	setCartOpen,
	setCartQuantity,
	setCheckoutAttempt,
	setCheckoutDetails,
	setPortalTenant,
	setSelectedLocation,
	toggleCartOpen,
	toggleFavorite,
	updateCartQuantity
} = portalSlice.actions;

// endregion

// region Selectors

const selectPortal = (state: { portal: PortalState }) => state.portal;

export const selectCartItems = (state: { portal: PortalState }) => selectPortal(state).cart;

export const selectCartItemCount = (state: { portal: PortalState }) => selectPortal(state).cart.length;

export const selectCartQuantityBySku = (sku: string) => (state: { portal: PortalState }) =>
	selectCartItems(state).find((item) => item.sku === sku)?.quantity ?? 0;

export const selectCartOpen = (state: { portal: PortalState }) => selectPortal(state).cartOpen;

export const selectCheckoutAttempt = (state: { portal: PortalState }) => selectPortal(state).checkoutAttempt ?? null;

export const selectCheckoutDetails = (state: { portal: PortalState }) => selectPortal(state).checkoutDetails ?? null;

export const selectFavorites = (state: { portal: PortalState }) => selectPortal(state).favorites;

export const selectRecentlyViewed = (state: { portal: PortalState }) => selectPortal(state).recentlyViewed;

export const selectSelectedLocationId = (state: { portal: PortalState }) => selectPortal(state).selectedLocationId;

// endregion

export default portalSlice.reducer;
